import React, { createContext, useEffect, useMemo, useState } from 'react'
import type { OAuthProvider, SerializedUser } from '../services/auth'
import * as auth from '../services/auth'
import { logger } from '../utils/logger'
import { hasValidAuthToken } from '../utils/authToken'

export type AuthContextType = {
  user: SerializedUser | null
  isAuthenticated: boolean
  isPremium?: boolean
  loading: boolean
  login: (provider: OAuthProvider, isSignup?: boolean) => Promise<void> | void
  logout: () => void
  oauthComplete: (user: SerializedUser) => void
  refreshUser: () => Promise<void>
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

// eslint-disable-next-line react-refresh/only-export-components
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<SerializedUser | null>(null)
  const [loading, setLoading] = useState(true)
  

  
  // Load user from localStorage on mount and verify token freshness
  useEffect(() => {
    const storedUser = auth.getUser()

    if (storedUser && !hasValidAuthToken()) {
      auth.logout()
      setUser(null)
      setLoading(false)
      return
    }

    setUser(storedUser)
    setLoading(false)
  }, [])

  useEffect(() => {
    // Sync across tabs
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'auth:user') setUser(auth.getUser())
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  const value = useMemo<AuthContextType>(() => {
    const loginWrapper = (p: OAuthProvider, isSignup?: boolean) => {
      logger.debug('Auth login initiated', { provider: p, isSignup })
      if (typeof auth.login !== 'function') {
        logger.error('auth.login is not a function', { loginType: typeof auth.login })
        throw new Error('auth.login is not a function')
      }
      return auth.login(p, isSignup)
    }
    
    const refreshUserWrapper = async () => {
      logger.debug('Refreshing user data from backend')
      const freshUser = await auth.refreshUser()
      if (freshUser) {
        setUser(freshUser)
        logger.info('User state updated with fresh data', { 
          userId: freshUser.id, 
          subscription: freshUser.subscription 
        })
      }
    }
    
    return {
      user,
      isAuthenticated: !!user,
      isPremium: user?.subscription === 'premium',
      loading,
      login: loginWrapper,
      logout: () => { auth.logout(); setUser(null) },
      oauthComplete: (user: SerializedUser) => {
        logger.debug('OAuth completion - immediate state update', { hasUser: !!user })
        setUser(user) // Immediate React state update
        logger.debug('User state updated after OAuth', { hasUser: !!user, user })
      },
      refreshUser: refreshUserWrapper,
    }
  }, [user, loading])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}


